import { FieldArray, FieldArrayRenderProps, useField } from 'formik';
import { debounce } from 'lodash';
import React, { useCallback, useContext, useRef } from 'react';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../../helpers/nullHelpers';
import { ReactComponent as DeleteIcon } from '../../../../../images/icons/delete.svg';
import { ButtonIcon, SecondaryButton } from '../../../../../shared/Button';
import { TextInputField } from '../../../../../shared/form/inputs/TextInputField';
import { Header3 } from '../../../../../shared/Headers';
import { Table, TBody, Td, Th, THead, Tr } from '../../../../../shared/Table';
import { spacing04, spacing06 } from '../../../../../styling/spacing';
import { DesignFormDefaultsContext } from '../../../../designs/DesignFormDefaultsContext';
import { getNewRoomWallHeatLossDetailsFormModel } from '../../rooms/heatLoss/roomWallHeatLossDetailsFormModel';
import { UnitFormModel } from '../unitFormModel';
import { onUnitWallTemperatureChange } from './unitElementOnChangeHelpers';
import { getUnitDefaultExternalAirTemperature } from './unitHeatLossDetailsFormModel';
import { getDefaultUnitWallElementFormModel } from './unitWallElementFormModel';

type Props = {
  externalAirTemperatureInDegreesCelsiusString: string;
  unit: UnitFormModel;
  unitIndex: number;
};

export const UnitWallElementsField = ({ externalAirTemperatureInDegreesCelsiusString, unit, unitIndex }: Props) => {
  const wallElementsFieldName = `units.${unitIndex}.heatLossDetails.wallElements`;

  const { unitWallElements, setUnitWallElements } = useContext(DesignFormDefaultsContext);
  const [defaultExternalAirTemperatureField] = useField(getUnitDefaultExternalAirTemperature(unitIndex));
  const defaultExternalAirTemperatureInDegreesCelsius =
    defaultExternalAirTemperatureField.value ?? externalAirTemperatureInDegreesCelsiusString;

  const wallElementFieldName = (wallElementIndex: number, fieldName: string) =>
    `${wallElementsFieldName}.${wallElementIndex}.${fieldName}`;

  const { heatLossDetails } = unit;

  const newWallElementNumber = useRef(heatLossDetails == null ? 1 : heatLossDetails.wallElements.length + 1);

  const addWallElement = (fieldArrayRenderProps: FieldArrayRenderProps) => {
    const newWallElement = {
      ...getDefaultUnitWallElementFormModel(defaultExternalAirTemperatureInDegreesCelsius),
      name: `Wall Element ${newWallElementNumber.current}`,
    };

    newWallElementNumber.current++;

    fieldArrayRenderProps.push(newWallElement);

    const newWallManifoldTemperatures: Array<Array<string>> = [];
    unit.manifolds.forEach((manifold, manifoldIndex) => {
      const rooms = manifold.rooms || [];
      const newWallRoomTemperatures: Array<string> = [];

      rooms.forEach((room, roomIndex) => {
        const roomHeatLossDetails = assertNotNull(room.heatLossDetails);

        const { walls } = roomHeatLossDetails;
        const newRoomWallDetailsForm = getNewRoomWallHeatLossDetailsFormModel(
          externalAirTemperatureInDegreesCelsiusString,
        );

        fieldArrayRenderProps.form.setFieldValue(
          `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails.walls`,
          [...walls, newRoomWallDetailsForm],
        );

        newWallRoomTemperatures.push(newRoomWallDetailsForm.externalAirTemperatureInDegreesCelsius);
      });

      newWallManifoldTemperatures.push(newWallRoomTemperatures);
    });

    const newUnitWallElement = {
      value: newWallElement.defaultExternalAirTemperatureInDegreesCelsius,
      manifoldRoomValues: newWallManifoldTemperatures,
    };

    setUnitWallElements([...unitWallElements, newUnitWallElement]);
  };

  const removeWallElement = (wallElementIndex: number, fieldArrayRenderProps: FieldArrayRenderProps) => {
    fieldArrayRenderProps.remove(wallElementIndex);

    unit.manifolds.forEach((manifold, manifoldIndex) => {
      const rooms = manifold.rooms || [];

      rooms.forEach((room, roomIndex) => {
        const roomHeatLossDetails = assertNotNull(room.heatLossDetails);

        const { walls } = roomHeatLossDetails;

        fieldArrayRenderProps.form.setFieldValue(
          `units.${unitIndex}.manifolds.${manifoldIndex}.rooms.${roomIndex}.heatLossDetails.walls`,
          walls.filter((_, wallIndex) => wallIndex !== wallElementIndex),
        );
      });
    });

    setUnitWallElements(unitWallElements.filter((_, index) => index !== wallElementIndex));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedTemperatureChange = useCallback(
    debounce((newValue: string, wallIndex: number, fieldArrayRenderProps: FieldArrayRenderProps) => {
      onUnitWallTemperatureChange(
        newValue,
        unitIndex,
        wallIndex,
        fieldArrayRenderProps.form,
        unitWallElements,
        setUnitWallElements,
      );
    }, 300),
    [unitWallElements, setUnitWallElements, unitIndex],
  );

  const handleTemperatureChange = (
    newValue: string,
    wallIndex: number,
    fieldArrayRenderProps: FieldArrayRenderProps,
  ) => {
    debouncedTemperatureChange(newValue, wallIndex, fieldArrayRenderProps);
  };

  if (heatLossDetails == null) {
    return null;
  }

  return (
    <FieldArray name={`units.${unitIndex}.heatLossDetails.wallElements`}>
      {fieldArrayRenderProps => (
        <Container>
          <Header3>Wall Elements</Header3>
          <Table>
            <THead>
              <Tr>
                <Th>Name</Th>
                <Th>Wall U-Value</Th>
                <Th>Window U-Value</Th>
                <Th>Door U-Value</Th>
                <Th>Default External Air Temp (°C)</Th>
                <DeleteButtonTableHeader />
              </Tr>
            </THead>
            <TBody>
              {heatLossDetails.wallElements.map((_, index) => (
                <Tr key={index}>
                  <Td>
                    <TextInputField name={wallElementFieldName(index, 'name')} label={null} />
                  </Td>
                  <Td>
                    <TextInputField name={wallElementFieldName(index, 'wallThermalTransmittance')} label={null} />
                  </Td>
                  <Td>
                    <TextInputField name={wallElementFieldName(index, 'windowThermalTransmittance')} label={null} />
                  </Td>
                  <Td>
                    <TextInputField name={wallElementFieldName(index, 'doorThermalTransmittance')} label={null} />
                  </Td>
                  <Td>
                    <TextInputField
                      name={wallElementFieldName(index, 'defaultExternalAirTemperatureInDegreesCelsius')}
                      label={null}
                      onChange={newValue => handleTemperatureChange(newValue, index, fieldArrayRenderProps)}
                    />
                  </Td>
                  <Td>
                    {heatLossDetails.wallElements.length > 1 && (
                      <SecondaryButton
                        onClick={() => removeWallElement(index, fieldArrayRenderProps)}
                        data-testid={deleteWallElementButtonTestId(index)}
                      >
                        <ButtonIcon>
                          <DeleteIcon />
                        </ButtonIcon>
                      </SecondaryButton>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
          <AddWallElementButton onClick={() => addWallElement(fieldArrayRenderProps)}>
            + Add Wall Element
          </AddWallElementButton>
        </Container>
      )}
    </FieldArray>
  );
};

export const deleteWallElementButtonTestId = (wallElementIndex: number) =>
  `delete-wall-element-button-${wallElementIndex}`;

const Container = styled.div`
  margin-bottom: ${spacing06};
`;

const DeleteButtonTableHeader = styled(Th)`
  width: 65px;
`;

const AddWallElementButton = styled(SecondaryButton)`
  margin-top: ${spacing04};
`;
